import React, {ReactNode, useEffect, useState} from "react";
import {Alert, Button, Dropdown, Flex, MenuProps, Tooltip} from "antd";
import {useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import {IconBoltGrad} from "../../components/icons/faBoltGrad";
import {openAccountSettings, openProjectSettings} from "../../feature/project/appSettingsSlice";
import {MenuItemType} from "antd/es/menu/interface";

export const GenButton = (
    props: {
        icon?: ReactNode,
        loading?: boolean,
        actions: {
            key: string,
            title: string,
            action: (model: string, projectId: string) => Promise<void>,
            render?: (key: string, title: string, action: (model: string, projectId: string) => Promise<void>) => ReactNode
        }[],
        type?: "primary" | "default" | "dashed" | "link" | "text" | undefined,
        style?: React.CSSProperties,
        mode?: 'icon' | 'button',
        hideMainAction?: boolean,
        disabled?: boolean,
        open?: boolean
    }
) => {
    const dispatch = useAppDispatch()
    const [loading, setLoading] = useState(props.loading)
    const model = useAppSelector(state => state.draftSlice.model)
    const [open, setOpen] = useState(false)
    const {projectId} = useParams()
    const aiProviders = useAppSelector(state => state.aiSlice.aiProviderSettings)
    const projectAiProviders = useAppSelector(state => state.aiSlice.projectAiProviderSettings)
    const [canGenerate, setCanGenerate] = useState(false)
    const [enabledProviders, setEnabledProviders] = useState<string[]>([])
    const [selectedProviders, setSelectedProviders] = useState<string[]>([])
    const [selectedModels, setSelectedModels] = useState<string[]>([])
    const user = useAppSelector(state => state.users.currentUser)

    useEffect(() => {
        const selectedProviders = projectAiProviders.filter(p => p.enabled).map(p => p.name)
        const enabledProviders = aiProviders.filter(p => p.enabled)
        const selectedModels = enabledProviders.map(p => p.models).flat().filter(m => m.trim().length > 0)
        setEnabledProviders(enabledProviders.map(p => p.name))
        setSelectedProviders(selectedProviders)
        setSelectedModels(selectedModels)
        setCanGenerate((enabledProviders.length > 0 && selectedProviders.length > 0 && selectedModels.length > 0) || (selectedProviders.length > 0 && user?.role != 'creator'))

    }, [aiProviders, projectAiProviders])

    useEffect(() => {
        setOpen(props.open == true && open)
    }, [props.open]);

    useEffect(() => {
        setLoading(props.loading)
    }, [props.loading]);

    const modelsMenu: MenuProps = {
        items: props.actions.length > 1 ? props.actions.slice(!props.hideMainAction ? 1 : 0, props.actions.length).map(action => ({
            key: action.key,
            label: action.render?.(action.key, action.title, action.action) ?? action.title,
            action: action.action
        } as MenuItemType)) : (props.hideMainAction ? props.actions.map(action => ({
            key: action.key,
            label: action.render?.(action.key, action.title, action.action) ?? action.title,
            action: action.action
        } as MenuItemType)) : []),
        onClick: async (item) => {
            setLoading(true)
            await props.actions.find(action => action.key == item.key)?.action(model, projectId ?? '')
            setLoading(false)
        }

    }

    return (

        props.actions.length == 0 ?
            <Alert style={{height: 36}} showIcon={true} type={'error'} message={'No actions defined'}
                   icon={<IconBoltGrad/>}/> :
            <Tooltip
                title={!canGenerate ? <>To use AI generation you need to:
                {enabledProviders.length == 0 || selectedModels.length == 0 ? <>{enabledProviders.length == 0 && selectedModels.length == 0 ? <><br/>- enable at least one AI Provider and select at least one model of enabled Ai Provider</> : selectedModels.length == 0 ? <><br/>- select at least one model of enabled AI Provider</> : ''}<br/>
                    <Button
                        size={'small'}
                        onClick={() => dispatch(openAccountSettings('ai'))}
                    >Account Settings
                    </Button></> : ''}
                {selectedProviders.length == 0 ? <><br/>- select at least one AI Provider<br/>
                    <Button size={'small'}
                            onClick={() => dispatch(openProjectSettings('ai'))}
                    >
                        Project Settings
                    </Button></> : ''}</> : props.actions.length == 1 ? props.actions[0].title : undefined}
            >
                <Dropdown.Button
                    style={{width: 'auto'}}
                    trigger={["click"]}
                    menu={modelsMenu}
                    onClick={async () => {
                        setLoading(true)
                        await props.actions[0].action(model, projectId ?? '')
                        setLoading(false)
                    }}
                    open={open}
                    onOpenChange={(open) => setOpen(open)}
                    type={props.type}
                    disabled={props.disabled || !canGenerate}
                    buttonsRender={([leftButton, rightButton]) => [
                        props.actions.length == 1 || props.hideMainAction ? undefined : React.cloneElement(leftButton as React.ReactElement<any, string>, {
                            style: {
                                ...props.style,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            },
                        }),
                        props.actions.length == 1 && !props.hideMainAction || loading ? React.cloneElement(leftButton as React.ReactElement<any, string>, {
                            style: {
                                ...props.style,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            },
                            loading: loading
                        }) : React.cloneElement(rightButton as React.ReactElement<any, string>, {
                            style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            },
                            icon: props.hideMainAction ? props.icon ?? (rightButton as React.ReactElement<any, string>).props.icon : (rightButton as React.ReactElement<any, string>).props.icon
                        })
                    ]}
                >
                    {loading && props.mode == 'icon' ?
                        ''
                        :
                        <Flex gap={8} align={'center'} justify={'center'}>{!loading ? (props.icon ?? <IconBoltGrad
                            colorStart={props.type == 'primary' ? (canGenerate ? "#fff" : "#ccc") : canGenerate ? undefined : "#ccc"}/>) : ''}{props.mode != 'icon' ? props.actions[0].title : undefined}
                        </Flex>
                    }
                </Dropdown.Button>
            </Tooltip>
    )
}